import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const ContentTab = ({ issue, setIssue, onSave, mockLlm, readOnly = false }) => {
  const [regenSection, setRegenSection] = useState(null);

  const upd = (k, v) => setIssue((s) => ({ ...s, [k]: v }));
  const updNested = (parent, k, v) => setIssue((s) => ({ ...s, [parent]: { ...(s[parent] || {}), [k]: v } }));

  const regen = async (section, guidance = "") => {
    setRegenSection(section);
    try {
      const r = await api.post(`/admin/issues/${issue.id}/action`, {
        action: "regenerate_section", payload: { section, guidance },
      });
      setIssue(r.data.issue);
      toast.success(`Regenerated ${section}`);
    } catch (e) {
      toast.error("Regenerate failed", { description: e?.response?.data?.detail || "" });
    } finally {
      setRegenSection(null);
    }
  };

  const RegenBtn = ({ section, guidance = "" }) => (
    <Button type="button" size="sm" variant="outline" onClick={() => regen(section, guidance)} disabled={regenSection === section} data-testid={`regen-${section}`}>
      <RefreshCw className={`h-3.5 w-3.5 mr-2 ${regenSection === section ? "animate-spin" : ""}`} />
      Regenerate
    </Button>
  );

  return (
    <fieldset disabled={readOnly} className="space-y-6 min-w-0 border-0 p-0 m-0 disabled:opacity-80" data-testid="content-tab-fieldset">
      {readOnly && (
        <div className="rounded-xl border border-border bg-secondary/60 px-4 py-3 text-sm text-muted-foreground" data-testid="content-tab-readonly-note">
          This issue is {issue.status === "published" ? "live" : issue.status}. Use <span className="font-medium text-foreground">Revert to in review</span> above to make changes.
        </div>
      )}
      <Card className="p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">Title</label>
            <Input value={issue.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="issue-title-input" className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-sm font-medium">Slug</label>
            <Input value={issue.slug || ""} onChange={(e) => upd("slug", e.target.value)} data-testid="issue-slug-input" className="mt-1 h-11 font-mono text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium">Theme</label>
            <Input value={issue.theme || ""} onChange={(e) => upd("theme", e.target.value)} className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-sm font-medium">Issue number</label>
            <Input
              type="number"
              min={1}
              step={1}
              value={issue.issue_number ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  upd("issue_number", "");
                  return;
                }
                const n = parseInt(raw, 10);
                if (!Number.isNaN(n) && n >= 1) upd("issue_number", n);
              }}
              data-testid="issue-number-input"
              className="mt-1 h-11 font-mono"
            />
            <p className="mt-1 text-xs text-muted-foreground">Must be a unique positive integer. Changing it will renumber the archive entry.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium">Excerpt</label>
            <Textarea rows={2} value={issue.excerpt || ""} onChange={(e) => upd("excerpt", e.target.value)} className="mt-1" />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="kicker">Opening reflection</div>
          <RegenBtn section="opening_markdown" />
        </div>
        <Textarea rows={10} value={issue.opening_markdown || ""} onChange={(e) => upd("opening_markdown", e.target.value)} data-testid="issue-opening-input" className="mt-3 font-serif text-base leading-relaxed" />
        <p className="mt-2 text-xs text-muted-foreground">Warm, practical, calm. 150–300 words.</p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="kicker">The Reconnect Prompt</div>
          <RegenBtn section="reconnect_prompt" />
        </div>
        <div className="mt-3 grid gap-3">
          <Input placeholder="Headline" value={issue.reconnect_prompt?.headline || ""} onChange={(e) => updNested("reconnect_prompt", "headline", e.target.value)} className="h-11" />
          <Textarea rows={3} placeholder="Body" value={issue.reconnect_prompt?.body || ""} onChange={(e) => updNested("reconnect_prompt", "body", e.target.value)} />
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="kicker">Message template (What to Send)</div>
          <RegenBtn section="message_template" />
        </div>
        <Textarea rows={4} value={issue.message_template || ""} onChange={(e) => upd("message_template", e.target.value)} data-testid="issue-message-input" className="mt-3 font-mono text-sm" />
        <p className="mt-2 text-xs text-muted-foreground">Include [Name] and [specific thing] placeholders.</p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="kicker">The Weekly Challenge</div>
          <RegenBtn section="weekly_challenge" />
        </div>
        <div className="mt-3 grid gap-3">
          <Input placeholder="Headline" value={issue.weekly_challenge?.headline || ""} onChange={(e) => updNested("weekly_challenge", "headline", e.target.value)} className="h-11" />
          <div>
            <label className="text-sm font-medium">Steps (one per line)</label>
            <Textarea
              rows={4}
              value={(issue.weekly_challenge?.steps || []).join("\n")}
              onChange={(e) => updNested("weekly_challenge", "steps", e.target.value.split("\n").filter(x => x.trim()))}
              className="mt-1"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6" data-testid="issue-quote-card">
        <div className="flex items-center justify-between">
          <div className="kicker">Inspiring quote</div>
          <RegenBtn section="inspiring_quote" />
        </div>
        <div className="mt-3 grid gap-3">
          <Textarea
            rows={2}
            placeholder="Quote text (a real, well-known quote)"
            value={issue.inspiring_quote?.text || ""}
            onChange={(e) => updNested("inspiring_quote", "text", e.target.value)}
            data-testid="issue-quote-text-input"
            className="font-display italic text-base"
          />
          <Input
            placeholder="Author (e.g. Helen Keller)"
            value={issue.inspiring_quote?.author || ""}
            onChange={(e) => updNested("inspiring_quote", "author", e.target.value)}
            data-testid="issue-quote-author-input"
            className="h-11"
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Required. Use real, accurately attributed quotes only — no invented or paraphrased lines.</p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="kicker">Closing question</div>
          <RegenBtn section="closing_question" />
        </div>
        <Input value={issue.closing_question || ""} onChange={(e) => upd("closing_question", e.target.value)} className="mt-3 h-11" />
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button onClick={onSave} data-testid="issue-save-content" className="bg-[color:var(--coh-ink)] text-white hover:bg-[color:var(--coh-ink-2)]">Save content</Button>
      </div>
    </fieldset>
  );
};
